// src/utils/rxLabelUtils.ts

type RxLabelTranslator = (
  key: string,
  fallback?: string,
  values?: Record<string, unknown>
) => string;

const DAY_SHORT: Record<string, string> = {
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
  sun: "Sun",
};

function getTranslator(t?: RxLabelTranslator): RxLabelTranslator {
  return typeof t === "function" ? t : (_key, fallback = "") => fallback;
}

function normalizeDays(input: unknown, t?: RxLabelTranslator): string[] {
  const translate = getTranslator(t);
  if (!Array.isArray(input)) return [];
  return input
    .map((d) => String(d ?? "").trim().toLowerCase())
    .filter(Boolean)
    .map((d) => {
      const key = d.slice(0, 3);
      const fallback = DAY_SHORT[key] ?? (key.slice(0, 1).toUpperCase() + key.slice(1));
      return translate(`prescriptions.wizard.days.${key}`, fallback);
    });
}

export function selectRxFrequencyLabel(args: { schedule?: any; t?: RxLabelTranslator } = {}): string {
  const schedule = args.schedule ?? {};
  const t = getTranslator(args.t);
  const f = schedule.frequencyType;

  if (f === "as_needed") return t("prescriptions.wizard.frequency.asNeeded", "As needed");

  if (f === "daily") {
    const h = Number(schedule.everyHours || 0);
    const dailyLabel = t("prescriptions.wizard.frequency.daily", "Daily");
    if (h > 0) {
      return `${dailyLabel}, ${t(
        "prescriptions.schedule.everyHours",
        "{every} {hours}",
        {
          every: t("prescriptions.wizard.every", "Every"),
          hours: t("prescriptions.wizard.hoursValue", "{count} hours", { count: h }),
        },
      )}`;
    }
    return dailyLabel;
  }

  if (f === "cyclical") {
    const use = Number(schedule.cycleUseDays || 0);
    const pause = Number(schedule.cyclePauseDays || 0);
    const cyclicalLabel = t("prescriptions.wizard.frequency.cyclical", "Cyclical schedule");
    return use > 0
      ? `${cyclicalLabel}: ${t(
          "prescriptions.schedule.cycleDetail",
          "{useFor} {useDays}, {pauseFor} {pauseDays}",
          {
            useFor: t("prescriptions.wizard.useFor", "Use for"),
            useDays: `${use} ${t("prescriptions.wizard.day", "day")}`,
            pauseFor: t("prescriptions.wizard.pauseFor", "Pause for"),
            pauseDays: `${pause} ${t("prescriptions.wizard.day", "day")}`,
          },
        )}`
      : cyclicalLabel;
  }

  if (f === "specific_days") {
    const days = normalizeDays(schedule.specificDays, t);
    return days.length
      ? days.join(", ")
      : t("prescriptions.wizard.frequency.specificDays", "Specific days");
  }

  return "—";
}
